/**
 * Cómo se escribe un precio.
 *
 * El documento de Jesús del 06-08-2026 lo escribe "1.500 €" en las tres pantallas donde
 * sale el Nivel 3: el test, planes y la renovación.
 *
 * Y aquí hay una trampa que costó encontrar: `(1500).toLocaleString('es-ES')` devuelve
 * "1500", SIN punto, y no es un fallo. La convención española no agrupa los números de
 * cuatro cifras (por eso un año se escribe 1500 y no 1.500), y los datos de idioma la
 * aplican. Para un precio se quiere el punto igualmente, así que el separador se pone
 * aquí a mano en vez de confiar en el idioma del navegador.
 *
 * El valor no se toca: el catálogo manda (1500). Esto es solo cómo se pinta.
 */

// 1500 -> "1.500" · 297 -> "297" · 1499.5 -> "1.499,50" · 12345 -> "12.345"
export const formatEuros = (n) => {
    const num = Number(n || 0);
    const signo = num < 0 ? '-' : '';
    // LOS CÉNTIMOS, ENTEROS (Jesús, 11-09, punto 24). Aquí se cortaban los ceros del final
    // y 60,50 salía "60,5", que es medio precio escrito. Él lo pide «60,50 €». Se quitan
    // los dos decimales solo cuando el precio es redondo -- «450 €», no «450,00 €» --,
    // que es como los escribe él.
    const [entera, decimal] = Math.abs(num).toFixed(2).split('.');
    const conMillares = entera.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
    return signo + conMillares + (decimal === '00' ? '' : `,${decimal}`);
};

// 1500 -> "1.500 €"
//
// El espacio es INDIVISIBLE (`\u00a0`), que es como se escribe en español y además evita lo
// que se vio en la lista de Clientes: la columna es estrecha y «21,91 €/mes» se partía en
// dos líneas, con el número arriba y el símbolo abajo. Un precio no se parte.
export const euros = (n) => `${formatEuros(n)}\u00a0€`;

/**
 * EL MISMO DINERO EN TODO EL PANEL (Jesús, 11-09, punto 24).
 *
 * «1500€, 60.5€, 81.07€, 450€ -- punto decimal y sin espacio antes del euro. Y arriba, en
 * el mismo panel, 17.981 € bien puesto.» Los importes del panel iban tal cual los manda el
 * servidor, que es como los escribe un ordenador, no como se escriben en español; y los que
 * sí pasaban por un formateador tenían uno propio en cada pantalla. De ahí que convivieran
 * las dos formas en la misma pantalla.
 *
 * Aquí está una vez, en el mismo sitio donde ya vivía el precio de los planes, para que el
 * dinero se escriba igual lo mire quien lo mire.
 *
 * OJO con `toLocaleString('es-ES')` y con `Intl`: el español NO agrupa los números de cuatro
 * cifras (un año se escribe 1500), así que 1500 sale «1500 €» y él lo pide «1.500 €». Con
 * cinco cifras ya agrupaba solo, y por eso el 17.981 € sí estaba bien. El punto de los
 * miles se pone aquí a mano, arriba, en vez de confiar en el idioma del navegador.
 */

// Un importe que puede no estar. Sin número, el hueco. Y el CERO también es hueco: un
// precio de 0 en la ficha es «no se sabe», no «no paga», y escribir "0 €" es decir algo
// que nadie ha dicho. Quien tenga un cero de verdad que llamar, que llame a `euros`.
export const eurosOHueco = (n, hueco = '—') =>
    (n === null || n === undefined || n === '' || isNaN(Number(n)) || Number(n) === 0)
        ? hueco : euros(n);

// Los totales gordos del panel (el MRR, la factura del mes): sin céntimos, que ahí no
// aportan y estorban al comparar dos pantallas de un vistazo.
export const eurosRedondos = (n, hueco = '—') =>
    (n === null || n === undefined || n === '' || isNaN(Number(n)))
        ? hueco
        : `${formatEuros(Math.round(Number(n)))}\u00a0€`;

// Lo mismo para cualquier otro número que se enseñe (el contador de personas: 1.325).
// Existe para no volver a tirar de toLocaleString y quedarse otra vez sin el punto.
export const numero = formatEuros;
